/*!
 * SPDX-FileCopyrightText: 2024 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */
import svgFileOutline from '@mdi/svg/svg/file-outline.svg?raw';
import { FileListFilter, registerFileListFilter } from '@nextcloud/files';
import { t } from '@nextcloud/l10n';
import wrap from '@vue/web-component-wrapper';
import Vue from 'vue';
import FileListFilterType from '../components/FileListFilter/FileListFilterType.vue';
import { logger } from '../utils/logger.ts';
const tagName = 'files-file-list-filter-type';
class TypeFilter extends FileListFilter {
    currentInstance;
    currentPresets;
    displayName = t('files', 'Type');
    iconSvgInline = svgFileOutline;
    tagName = tagName;
    constructor() {
        super('files:type', 10);
        this.currentPresets = [];
    }
    filter(nodes) {
        if (!this.currentPresets || this.currentPresets.length === 0) {
            return nodes;
        }
        const mimeList = this.currentPresets.reduce((previous, current) => [...previous, ...current.mime], []);
        return nodes.filter((node) => {
            if (!node.mime) {
                return false;
            }
            const mime = node.mime.toLowerCase();
            if (mimeList.includes(mime)) {
                return true;
            }
            else if (mimeList.includes(window.OC.MimeTypeList.aliases[mime])) {
                return true;
            }
            else if (mimeList.includes(mime.split('/')[0])) {
                return true;
            }
            return false;
        });
    }
    reset() {
        // to be listener by the component
        this.dispatchEvent(new CustomEvent('reset'));
    }
    get presets() {
        return this.currentPresets;
    }
    setPresets(presets) {
        logger.debug('TypeFilter: setting presets', { presets });
        this.currentPresets = presets ?? [];
        if (this.currentInstance !== undefined) {
            // could be called before the instance was created
            // (meaning the files list is not mounted yet)
            this.currentInstance.$props.presets = presets;
        }
        this.filterUpdated();
        const chips = [];
        if (presets && presets.length > 0) {
            for (const preset of presets) {
                chips.push({
                    icon: preset.icon,
                    text: preset.label,
                    onclick: () => this.removeFilterPreset(preset.id),
                });
            }
        }
        else {
            this.currentInstance?.resetFilter();
        }
        this.updateChips(chips);
    }
    /**
     * Helper callback that removed a preset from selected.
     * This is used when clicking on "remove" on a filter-chip.
     *
     * @param presetId Id of preset to remove
     */
    removeFilterPreset(presetId) {
        const filtered = this.currentPresets.filter(({ id }) => id !== presetId);
        this.dispatchEvent(new CustomEvent('deselect', { detail: presetId }));
        this.setPresets(filtered);
    }
}
/**
 * Register the file list filter by file type
 */
export function registerTypeFilter() {
    const WrappedComponent = wrap(Vue, FileListFilterType);
    // In Vue 2, wrap doesn't support disabling shadow :(
    // Disable with a hack
    Object.defineProperty(WrappedComponent.prototype, 'attachShadow', {
        value() {
            return this;
        },
    });
    Object.defineProperty(WrappedComponent.prototype, 'shadowRoot', {
        get() {
            return this;
        },
    });
    window.customElements.define(tagName, WrappedComponent);
    registerFileListFilter(new TypeFilter());
}
